import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { ClipboardList, Plus } from 'lucide-react'
import {
  getEvaluations as getMocks,
  getEvaluationTemplates as getMockTemplates,
  createEvaluation as createMock,
  type EvaluationListItem as MockListItem,
  type EvaluationTemplate as MockTemplate,
} from '../../api/evaluations'
import { rosterApi, type Roster, type RosterStudent } from '../../api/roster'

export function MockMgr() {
  const navigate = useNavigate()
  const [mocks, setMocks] = useState<MockListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)

  // Modal state
  const [templates, setTemplates] = useState<MockTemplate[]>([])
  const [rosters, setRosters] = useState<Roster[]>([])
  const [rosterStudents, setRosterStudents] = useState<RosterStudent[]>([])
  const [title, setTitle] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [assignMode, setAssignMode] = useState<string | null>('roster')
  const [selectedRosterId, setSelectedRosterId] = useState<string | null>(null)
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    fetchMocks()
  }, [])

  function fetchMocks() {
    setLoading(true)
    getMocks()
      .then((res) => setMocks(res.data))
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load mocks', color: 'red' }))
      .finally(() => setLoading(false))
  }

  function openCreateModal() {
    setModalOpen(true)
    setTitle('')
    setSelectedTemplateId(null)
    setAssignMode('roster')
    setSelectedRosterId(null)
    setSelectedStudentId(null)

    // Fetch templates and rosters
    getMockTemplates().then((res) => setTemplates(res.data)).catch(() => {})
    rosterApi.list().then((res) => setRosters(res.data)).catch(() => {})
  }

  function handleRosterChange(rosterId: string | null) {
    setSelectedRosterId(rosterId)
    if (rosterId) {
      rosterApi.get(Number(rosterId)).then((res) => setRosterStudents(res.data.students)).catch(() => {})
    } else {
      setRosterStudents([])
    }
  }

  async function handleCreate() {
    if (!title.trim()) {
      notifications.show({ title: 'Error', message: 'Title is required', color: 'red' })
      return
    }
    if (!selectedTemplateId) {
      notifications.show({ title: 'Error', message: 'Select a mock template', color: 'red' })
      return
    }

    const payload: Parameters<typeof createMock>[0] = {
      title: title.trim(),
      evaluation_template_id: Number(selectedTemplateId),
    }

    if (assignMode === 'roster' && selectedRosterId) {
      payload.roster_id = Number(selectedRosterId)
    } else if (assignMode === 'student' && selectedStudentId) {
      payload.student_ids = [Number(selectedStudentId)]
    } else {
      notifications.show({ title: 'Error', message: 'Select students or a roster', color: 'red' })
      return
    }

    setCreating(true)
    try {
      await createMock(payload)
      notifications.show({ title: 'Success', message: 'Mock created', color: 'green' })
      setModalOpen(false)
      fetchMocks()
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to create mock', color: 'red' })
    } finally {
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <Box>
            <Title order={2} c="var(--sat-text)">Mocks</Title>
            <Text size="sm" c="var(--sat-text-muted)">Manage SAT mock tests for students.</Text>
          </Box>
          <Button
            leftSection={<Plus size={16} />}
            color="grapefruit"
            radius="xl"
            onClick={openCreateModal}
          >
            Create New Mock
          </Button>
        </Group>

        {mocks.length === 0 ? (
          <Paper radius="xl" p="xl" withBorder style={{ textAlign: 'center', background: 'var(--sat-surface)' }}>
            <ClipboardList size={48} color="var(--sat-blush)" style={{ margin: '0 auto 12px' }} />
            <Text fw={600} c="var(--sat-text)" mb={4}>No mocks yet</Text>
            <Text size="sm" c="var(--sat-text-muted)">Create a mock to get started.</Text>
          </Paper>
        ) : (
          <Paper radius="xl" p="md" withBorder style={{ background: 'var(--sat-surface)' }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Student</Table.Th>
                  <Table.Th>RW Score</Table.Th>
                  <Table.Th>Math Score</Table.Th>
                  <Table.Th>Date</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {mocks.map((mock) => (
                  <Table.Tr
                    key={mock.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tutor/mock/${mock.id}`)}
                  >
                    <Table.Td>
                      <Text fw={600} size="sm">{mock.title}</Text>
                    </Table.Td>
                    <Table.Td>{mock.user_name || '-'}</Table.Td>
                    <Table.Td>
                      {mock.rw_score != null ? (
                        <Badge color="blue" variant="light">{mock.rw_score}</Badge>
                      ) : (
                        <Badge color="gray" variant="light">Pending</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {mock.math_score != null ? (
                        <Badge color="grape" variant="light">{mock.math_score}</Badge>
                      ) : (
                        <Badge color="gray" variant="light">Pending</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="var(--sat-text-muted)">
                        {mock.create_datetime ? new Date(mock.create_datetime).toLocaleDateString() : '-'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Create New Mock"
        size="md"
        radius="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Title"
            placeholder="e.g. Midterm SAT Mock"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            required
          />

          <Select
            label="Mock Template"
            placeholder="Select a template"
            data={templates.map((t) => ({ value: String(t.id), label: t.title }))}
            value={selectedTemplateId}
            onChange={setSelectedTemplateId}
            required
          />

          <Select
            label="Assign To"
            data={[
              { value: 'roster', label: 'A Roster' },
              { value: 'student', label: 'A Single Student' },
            ]}
            value={assignMode}
            onChange={setAssignMode}
          />

          {assignMode === 'roster' && (
            <Select
              label="Select Roster"
              placeholder="Choose a roster"
              data={rosters.map((r) => ({ value: String(r.id), label: r.name }))}
              value={selectedRosterId}
              onChange={handleRosterChange}
            />
          )}

          {assignMode === 'student' && (
            <>
              <Select
                label="From Roster"
                placeholder="Choose roster first to see students"
                data={rosters.map((r) => ({ value: String(r.id), label: r.name }))}
                value={selectedRosterId}
                onChange={(val) => {
                  setSelectedRosterId(val)
                  setSelectedStudentId(null)
                  if (val) {
                    rosterApi.get(Number(val)).then((res) => setRosterStudents(res.data.students)).catch(() => {})
                  } else {
                    setRosterStudents([])
                  }
                }}
              />
              {rosterStudents.length > 0 && (
                <Select
                  label="Select Student"
                  placeholder="Choose a student"
                  data={rosterStudents.map((s) => ({
                    value: String(s.student_id),
                    label: s.student_name || s.student_email || `Student #${s.student_id}`,
                  }))}
                  value={selectedStudentId}
                  onChange={setSelectedStudentId}
                />
              )}
            </>
          )}

          <Button
            color="grapefruit"
            radius="xl"
            onClick={handleCreate}
            loading={creating}
            fullWidth
          >
            Create Mock
          </Button>
        </Stack>
      </Modal>
    </Container>
  )
}
